<div class="bill-review-step">

  <!-- Meter Context Breadcrumb -->
  @if (currentNav) {
    <div class="meter-context">
      <div class="breadcrumb">
        <span class="crumb">
          <i class="material-symbols-rounded">account_balance</i>
          {{ currentNav.lenName }}
        </span>
        <i class="material-symbols-rounded separator">chevron_right</i>
        <span class="crumb">
          <i class="material-symbols-rounded">home_work</i>
          {{ currentNav.saAddress }}
        </span>
        <i class="material-symbols-rounded separator">chevron_right</i>
        <span class="crumb active">
          <i class="material-symbols-rounded">speed</i>
          {{ currentNav.meterUan || 'Meter' }}
        </span>
      </div>
      <div class="meter-counter">
        Meter {{ currentMeterNumber }} of {{ totalMeterCount }}
      </div>
    </div>
  }

  <!-- Entity Mismatch Warnings -->
  @if (mismatchWarnings.length > 0) {
    <div class="mismatch-warnings">
      @for (warn of mismatchWarnings; track warn.field) {
        <div class="mismatch-warning">
          <i class="material-symbols-rounded">warning</i>
          <div class="mismatch-text">
            <strong>{{ warn.fieldLabel }} mismatch:</strong>
            Bill says "{{ warn.extractedValue }}" but you're onboarding under "{{ warn.expectedValue }}"
          </div>
        </div>
      }
    </div>
  }

  <!-- Duplicate Bill Warning -->
  @if (duplicateWarning) {
    <div class="duplicate-warning"
         [class.blocking]="duplicateWarning.blocking"
         [class.pairable]="duplicateWarning.pairable"
         [class.replacing]="duplicateWarning.tone === 'replace'">
      <i class="material-symbols-rounded">{{ duplicateWarning.icon }}</i>
      <div class="duplicate-warning-content">
        <strong>{{ duplicateWarning.label }}</strong>
        <span>{{ duplicateWarning.message }}</span>
        @if (duplicateWarning.pdfLinks?.length) {
          <div class="duplicate-pdf-links">
            @for (link of duplicateWarning.pdfLinks; track link.type) {
              <button type="button" class="duplicate-pdf-link" (click)="openExistingPdf(link.type)">
                <i class="material-symbols-rounded">picture_as_pdf</i>
                {{ link.label }}
              </button>
            }
          </div>
        }
      </div>
      @if (duplicateWarning.pairable) {
        <button class="pair-reject-btn" (click)="rejectPairing()"
                title="Not the same bill? Treat this as a separate bill instead.">
          Don't pair
        </button>
      }
      @if (duplicateWarning.actionLabel) {
        <button class="duplicate-action-btn" (click)="switchDuplicateAction()">
          {{ duplicateWarning.actionLabel }}
        </button>
      }
    </div>
  }

  <!-- Dual Billing Detected Banner -->
  @if (isDualBill) {
    <div class="dual-bill-banner" [class.merging]="isMerging">
      <div class="dual-bill-icon">
        <i class="material-symbols-rounded">merge_type</i>
      </div>
      <div class="dual-bill-content">
        <div class="dual-bill-title">
          @if (isMerging) {
            Merging Bills...
          } @else {
            Dual Billing Detected
          }
        </div>
        <div class="dual-bill-files">
          <span class="file-chip">
            <span class="file-chip-kind">Utility</span>
            <span class="file-chip-name">{{ utilityFileName }}</span>
          </span>
          <i class="material-symbols-rounded merge-arrow">add</i>
          <span class="file-chip">
            <span class="file-chip-kind">Supplier</span>
            <span class="file-chip-name">{{ supplierFileName }}</span>
          </span>
        </div>
        @if (mergeWarnings.length > 0) {
          <div class="merge-warnings">
            @for (warning of mergeWarnings; track warning) {
              <div class="merge-warning">
                <i class="material-symbols-rounded">warning</i>
                {{ warning }}
              </div>
            }
          </div>
        }
      </div>
    </div>
  }

  <!-- Bill Navigation Bar: the bill's commodity on the far left, the pager centred -->
  <div class="bill-nav-bar">
    <app-commodity-badge class="nav-commodity" [commodity]="billCommodity"></app-commodity-badge>
    <button class="nav-btn" (click)="navigatePrev()" [disabled]="isFirstBill" title="Previous bill">
      <i class="material-symbols-rounded">chevron_left</i>
    </button>
    <div class="nav-info">
      <i class="material-symbols-rounded">receipt_long</i>
      <span>Bill {{ currentBillNumber }} of {{ totalBillCount }}</span>
    </div>
    <button class="nav-btn" (click)="navigateNext()" [disabled]="isLastBill || !billForm.isFormValid()" title="Next bill">
      <i class="material-symbols-rounded">chevron_right</i>
    </button>
    <span class="nav-spacer" aria-hidden="true"></span>
  </div>

  <!-- Billing Method -->
  <div class="billing-method-row">
    <span class="billing-method-label">
      Billing Method
      <i class="material-symbols-rounded info-icon"
         [matTooltip]="'Consolidated: one combined utility bill.\nDual: separate utility and supplier bills (two PDFs).'"
         matTooltipClass="bill-warning-tooltip">info</i>
    </span>
    <mat-radio-group class="billing-method-group" [value]="billingMethod" (change)="onBillingMethodChange($event)">
      <!-- Tooltip lives on a wrapper so it still shows while the radio is disabled. -->
      <span class="consolidated-radio-wrap"
            matTooltip="You cannot consolidate with two bills."
            matTooltipClass="bill-warning-tooltip"
            [matTooltipDisabled]="!consolidatedDisabled">
        <mat-radio-button [value]="BillType.CONSOLIDATED" [disabled]="consolidatedDisabled">Consolidated</mat-radio-button>
      </span>
      <mat-radio-button [value]="BillType.DUAL">Dual</mat-radio-button>
    </mat-radio-group>
  </div>

  <!-- Bill Form -->
  <app-bill-form #billForm
    [billData]="billDataForForm"
    [isBudgetingMode]="billService.isBudgetingMode()"
    [dualBilling]="billingMethod === BillType.DUAL"
    [fieldErrors]="currentBillFieldErrors"
    [fieldWarnings]="currentBillFieldWarnings"
    [meterContext]="formMeterContext"
    (formChanged)="onFormChanged($event)"
    (commitRequested)="onFormCommit()">
  </app-bill-form>

  <!-- Validation Banner -->
  @if (validationSummary.totalErrors > 0) {
    <div class="validation-banner">
      <div class="validation-banner-title">
        <i class="material-symbols-rounded">block</i>
        Fix these before continuing
      </div>
      @for (group of validationSummary.errorGroups; track group.entityLabel) {
        <div class="validation-banner-group">
          <div class="group-label">{{ group.entityLabel }}</div>
          <ul>
            @for (field of group.fields; track field) {
              <li>{{ field }}</li>
            }
          </ul>
        </div>
      }
    </div>
  }
  @if (validationSummary.totalWarnings > 0) {
    <div class="validation-banner warn">
      <div class="validation-banner-title">
        <i class="material-symbols-rounded">warning</i>
        {{ validationSummary.totalWarnings }} warning{{ validationSummary.totalWarnings === 1 ? '' : 's' }}
      </div>
      @for (group of validationSummary.warningGroups; track group.entityLabel) {
        <div class="validation-banner-group">
          <div class="group-label">{{ group.entityLabel }}</div>
          <ul>
            @for (field of group.fields; track field) {
              <li>{{ field }}</li>
            }
          </ul>
        </div>
      }
    </div>
  }

  <!-- Actions -->
  <div class="actions-bar">
    <button class="btn-secondary" [class.btn-back-to-overview]="showBackToOverview()" (click)="showBackToOverview() ? onBackToOverview() : goBack()">
      <i class="material-symbols-rounded">arrow_back</i>
      Back
    </button>
    <button class="btn-undo" (click)="billForm.undo()" [disabled]="!billForm.canUndo()" title="Undo last change">
      <i class="material-symbols-rounded">undo</i>
      Undo
    </button>
    <button class="btn-primary" (click)="onNextClick()" [disabled]="!billForm.isFormValid()">
      {{ nextButtonLabel }}
      <i class="material-symbols-rounded">arrow_forward</i>
    </button>
  </div>
</div>
